import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { getClientFunctions } from "@/lib/firebase-client";
import { cn } from "@/lib/utils";
import { httpsCallable } from "firebase/functions";
import {
  AlertCircle,
  ArrowRightLeft,
  CheckCircle2,
  ImageOff,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Types ─────────────────────────────────────────────────────────────────

type DevBox = {
  id: string;
  title: string;
  ownerUid: string;
  ownerName?: string;
  recipeCount: number;
  imageUrl: string;
  isActive: boolean;
};

type BoxResult = {
  recipeBoxId: string;
  title: string;
  status: "success" | "error";
  error?: string;
  recipesCount: number;
  imagesCopied: number;
};

type MigrationResponse = {
  success: boolean;
  results: BoxResult[];
  totalRecipes: number;
  totalImages: number;
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function callFn<T>(name: string, data: unknown): Promise<T> {
  const functions = getClientFunctions();
  if (!functions) throw new Error("Firebase functions unavailable");
  return httpsCallable<unknown, T>(functions, name)(data).then((r) => r.data);
}

const isDevEnvironment =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// ─── Component ─────────────────────────────────────────────────────────────

export default function AdminDataMigrationPage() {
  const breadcrumbs = useMemo(
    () => [
      { label: "Recipe Boxes", href: "/home" },
      { label: "Admin", href: "/admin" },
      { label: "Data Migration" },
    ],
    [],
  );
  usePageBreadcrumbs(breadcrumbs);

  const { user, profile, loading: authLoading } = useAuthProfile();
  const navigate = useNavigate();

  const [boxes, setBoxes] = useState<DevBox[]>([]);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [migrating, setMigrating] = useState(false);
  const [results, setResults] = useState<MigrationResponse | null>(null);
  const [migrationError, setMigrationError] = useState<string | null>(null);

  const loadedRef = useRef(false);

  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
    else if (!authLoading && user && profile && !profile.isAdmin) navigate("/home");
  }, [authLoading, user, profile, navigate]);

  // Load dev boxes via the listDevBoxes function (runs in prod, reads from dev)
  const loadBoxes = useCallback(async () => {
    setLoadingBoxes(true);
    setLoadError(null);
    try {
      const response = await callFn<{ boxes: DevBox[] }>("listDevBoxes", null);
      setBoxes(response.boxes);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Failed to load dev recipe boxes");
    } finally {
      setLoadingBoxes(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user && profile?.isAdmin && !loadedRef.current) {
      loadedRef.current = true;
      void loadBoxes();
    }
  }, [authLoading, user, profile, loadBoxes]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === boxes.length) setSelected(new Set());
    else setSelected(new Set(boxes.map((b) => b.id)));
  };

  const handleMigrate = async () => {
    if (selected.size === 0) return;
    setMigrating(true);
    setResults(null);
    setMigrationError(null);
    try {
      const response = await callFn<MigrationResponse>("migrateToProduction", {
        recipeBoxIds: [...selected],
      });
      setResults(response);
    } catch (error) {
      setMigrationError(error instanceof Error ? error.message : "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user || !profile?.isAdmin) return null;

  const allSelected = boxes.length > 0 && selected.size === boxes.length;
  const someSelected = selected.size > 0 && !allSelected;

  return (
    <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <ArrowRightLeft className="h-8 w-8 text-primary" />
          <h1 className="font-headline text-3xl font-semibold tracking-tight">Data Migration</h1>
        </div>
        <p className="text-muted-foreground">
          Pull recipe boxes, recipes, and images from{" "}
          <span className="font-medium text-foreground">development</span> into{" "}
          <span className="font-medium text-foreground">production</span>.
        </p>
      </div>

      {/* Environment warning */}
      {isDevEnvironment && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You are on localhost (dev). This migration tool must be accessed from the{" "}
            <strong>production</strong> web app — the functions run in the production project and
            pull data from dev.
          </AlertDescription>
        </Alert>
      )}

      {/* Secret setup reminder */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Before using this tool, ensure the{" "}
          <code className="font-mono text-sm">DEV_SERVICE_ACCOUNT_KEY</code> Firebase secret is set
          in the <strong>production</strong> project:
          <br />
          <code className="font-mono text-sm block mt-1">
            cat dev-service-account.json | base64 | pbcopy
            <br />
            firebase functions:secrets:set DEV_SERVICE_ACCOUNT_KEY --project ezrecipebox-main
          </code>
          <span className="text-muted-foreground text-sm block mt-1">
            Value should be the base64-encoded JSON of the development service account key.
          </span>
        </AlertDescription>
      </Alert>

      {/* Dev recipe box selection */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Development Recipe Boxes</CardTitle>
            <CardDescription>
              Select which boxes to pull into production. Existing documents with the same ID will
              be overwritten.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              loadedRef.current = false;
              void loadBoxes();
            }}
            disabled={loadingBoxes}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", loadingBoxes && "animate-spin")} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {loadError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
          )}

          {loadingBoxes ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : boxes.length === 0 && !loadError ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No recipe boxes found in the development project.
            </p>
          ) : (
            <>
              {/* Select all row */}
              <div className="flex items-center gap-3 pb-2 border-b">
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  data-state={someSelected ? "indeterminate" : allSelected ? "checked" : "unchecked"}
                  onCheckedChange={toggleSelectAll}
                />
                <label
                  htmlFor="select-all"
                  className="text-sm font-medium cursor-pointer select-none"
                >
                  Select all ({boxes.length})
                </label>
              </div>

              {/* Box rows */}
              {boxes.map((box) => (
                <div
                  key={box.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors",
                    selected.has(box.id)
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50",
                  )}
                  onClick={() => toggleSelect(box.id)}
                >
                  <Checkbox
                    id={`box-${box.id}`}
                    checked={selected.has(box.id)}
                    onCheckedChange={() => toggleSelect(box.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {box.imageUrl ? (
                    <img
                      src={box.imageUrl}
                      alt={box.title}
                      className="h-10 w-10 rounded object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                      <ImageOff className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{box.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      Owner: {box.ownerName ?? box.ownerUid}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="secondary">{box.recipeCount} recipes</Badge>
                    {!box.isActive && (
                      <Badge variant="outline" className="text-muted-foreground">
                        inactive
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {/* Migrate button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {selected.size > 0
            ? `${selected.size} box${selected.size !== 1 ? "es" : ""} selected`
            : "No boxes selected"}
        </p>
        <Button
          onClick={() => void handleMigrate()}
          disabled={selected.size === 0 || migrating}
          className="min-w-[180px]"
        >
          {migrating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Pulling…
            </>
          ) : (
            <>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Pull into Production
            </>
          )}
        </Button>
      </div>

      {/* Migration error */}
      {migrationError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{migrationError}</AlertDescription>
        </Alert>
      )}

      {/* Results */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {results.success ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-destructive" />
              )}
              Migration {results.success ? "Completed" : "Completed with Errors"}
            </CardTitle>
            <CardDescription>
              {results.totalRecipes} recipe{results.totalRecipes !== 1 ? "s" : ""} copied,{" "}
              {results.totalImages} image{results.totalImages !== 1 ? "s" : ""} copied
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {results.results.map((r) => (
              <div
                key={r.recipeBoxId}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-md border text-sm",
                  r.status === "success"
                    ? "border-green-200 bg-green-50"
                    : "border-red-200 bg-red-50",
                )}
              >
                {r.status === "success" ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{r.title}</p>
                  {r.status === "success" ? (
                    <p className="text-muted-foreground">
                      {r.recipesCount} recipe{r.recipesCount !== 1 ? "s" : ""},{" "}
                      {r.imagesCopied} image{r.imagesCopied !== 1 ? "s" : ""} copied
                    </p>
                  ) : (
                    <p className="text-red-600">{r.error}</p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
